import { useCallback, useState } from 'react';
import type { Reference } from '@apollo/client';
import { useMutation } from '@apollo/client';
import { isFunction } from 'lodash-es';
import type {
    CreateMeasuringPointComment,
    MeasuringPointCommentsQuery,
    MeasuringPointCommentType,
    MutationCreateMeasuringPointCommentArgs,
} from '@/graphql/types';
import {
    CREATE_MEASURING_POINT_COMMENT_MUTATION,
    MEASURING_POINT_COMMENT_FRAGMENT,
    MEASURING_POINT_COMMENTS_QUERY,
} from './queries';
import { getClient } from '../../../utils/apollo-client';

export function useCreateComment(
    onSuccess: ((comment: MeasuringPointCommentType) => void) | null = null
) {
    const [newCommentText, setNewCommentText] = useState('');

    const [createCommentMutation, { loading, error }] = useMutation<
        { createMeasuringPointComment: CreateMeasuringPointComment },
        MutationCreateMeasuringPointCommentArgs
    >(CREATE_MEASURING_POINT_COMMENT_MUTATION, {
        update(cache, { data }, { variables }) {
            const measuringPointComment = data?.createMeasuringPointComment?.measuringPointComment;
            const parentId = variables?.parentId;

            if (!measuringPointComment) {
                return;
            }

            const parentCommentRef = cache.identify({
                __typename: measuringPointComment.__typename,
                id: parentId,
            });

            if (parentId && parentCommentRef) {
                // Update replies for an existing comment.
                cache.modify({
                    id: parentCommentRef,
                    fields: {
                        replies(existingReplies: readonly Reference[] = []): Reference[] {
                            const newCommentRef = cache.writeFragment({
                                data: measuringPointComment,
                                fragment: MEASURING_POINT_COMMENT_FRAGMENT,
                            });
                            return [...existingReplies, ...(newCommentRef ? [newCommentRef] : [])];
                        },
                    },
                });
            } else {
                // Add new top-level comment to the measuringPointComments query.
                const { measuringPointId } = measuringPointComment;
                const measuringPointCommentsQuery = cache.readQuery<MeasuringPointCommentsQuery>({
                    query: MEASURING_POINT_COMMENTS_QUERY,
                    variables: {
                        measuringPointId,
                        withReplies: false,
                    },
                });

                if (
                    measuringPointCommentsQuery &&
                    measuringPointCommentsQuery.measuringPointComments
                ) {
                    cache.writeQuery<MeasuringPointCommentsQuery>({
                        query: MEASURING_POINT_COMMENTS_QUERY,
                        variables: {
                            measuringPointId,
                            withReplies: false,
                        },
                        data: {
                            measuringPointComments: [
                                ...measuringPointCommentsQuery.measuringPointComments,
                                {
                                    ...measuringPointComment,
                                    replies: [],
                                },
                            ],
                        },
                    });
                }
            }
        },
        client: getClient(),
        onCompleted(data) {
            const measuringPointComment = data.createMeasuringPointComment?.measuringPointComment;
            if (measuringPointComment && isFunction(onSuccess)) {
                onSuccess(measuringPointComment);
            }
            setNewCommentText('');
        },
    });

    const createComment = useCallback(
        async (partialComment: MutationCreateMeasuringPointCommentArgs) => {
            const trimmedText = newCommentText.trim();

            if (!trimmedText.trim()) {
                return;
            }

            await createCommentMutation({
                variables: {
                    ...partialComment,
                    text: trimmedText,
                },
            });
        },
        [createCommentMutation, newCommentText]
    );

    return {
        isLoading: loading,
        error,
        newCommentText,
        setNewCommentText,
        createComment,
    };
}
